"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function ThePrintingJourney() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: stepsRef,
    isVisible: stepsVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const steps = [{
    number: "01",
    title: "DIGITIZE",
    caption: "ARTWORK · COLOR · PROFILE",
    description: "Upload your high-resolution designs. Our team optimizes your files for precise color reproduction, ensuring every detail is ready for the film transfer process."
  }, {
    number: "02",
    title: "TRANSFER",
    caption: "FILM · INK · PRECISION",
    description: "We print your designs onto premium DTF film using fast-dry, vibrant inks. Our industrial-grade printers ensure sharp lines and consistent color density every time."
  }, {
    number: "03",
    title: "PRESS",
    caption: "HEAT · BOND · DURABILITY",
    description: "Using professional heat-press technology, we bond your custom transfers to apparel. The result is a durable, stretch-resistant finish that stands the test of time."
  }];
  return <section data-section-id="1283" id="the-printing-journey" className="relative bg-background text-foreground overflow-hidden py-32 md:py-44">
      {}
      <div className="absolute top-0 left-0 right-0 h-px overflow-visible pointer-events-none">
        <div className="absolute left-0 right-0 top-0 h-px bg-primary origin-left" style={{
        transform: "rotate(-6deg) scaleX(1)",
        transformOrigin: "left center"
      }} />
        <div className="absolute right-0 top-0 w-1.5 h-1.5 bg-primary" />
      </div>

      {}
      <div aria-hidden="true" className="pointer-events-none select-none absolute inset-0 flex items-center justify-center">
        <span className="font-sans text-foreground/[0.06] leading-none" style={{
        fontSize: "clamp(18rem, 32vw, 36rem)"
      }}>
          02
        </span>
      </div>

      <div className="relative max-w-[1440px] mx-auto px-6 md:px-12">
        {}
        <div ref={headerRef} className={`flex flex-col items-center text-center transition-all duration-[520ms] ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`} style={{
        transitionTimingFunction: "cubic-bezier(0.2, 0.8, 0.2, 1)"
      }}>
          {}
          <div className="flex items-center gap-4 mb-8">
            <span className="font-mono text-muted-foreground uppercase" style={{
            fontSize: "clamp(0.75rem, 0.9vw, 0.875rem)",
            letterSpacing: "0.18em"
          }}>
              § 02 / PROCESS
            </span>
            <span className="block w-10 h-px bg-border" />
          </div>

          <h2 className="font-default text-foreground uppercase" style={{
          fontSize: "clamp(3rem, 8vw, 7.5rem)",
          letterSpacing: "-0.015em",
          lineHeight: "0.86"
        }}>
            THE PRINTING{" "}
            <span className="font-serif italic" style={{
            color: "var(--primary)",
            fontSize: "0.85em",
            letterSpacing: "0.01em"
          }}>
              Journey
            </span>
          </h2>

          <p className="mt-8 max-w-2xl font-sans text-muted-foreground" style={{
          fontSize: "clamp(1.0625rem, 1.2vw, 1.25rem)",
          lineHeight: "1.55",
          letterSpacing: "-0.005em"
        }}>
            Three steps. One seamless workflow. The technical precision behind every DTFPRINT order.
          </p>
        </div>

        {}
        <div ref={stepsRef} className="mt-24 md:mt-32 grid grid-cols-1 md:grid-cols-12 gap-y-16 md:gap-x-8 relative">
          {steps.map((step, i) => <div data-index={i} key={step.number} className="md:col-span-4 relative">
              {}
              {i < steps.length - 1 && <>
                  {}
                  <div className="hidden md:block absolute top-12 right-0 translate-x-1/2 w-24 lg:w-32 h-px pointer-events-none overflow-visible z-10">
                    <div className="absolute inset-0 bg-primary origin-left transition-transform duration-700 ease-out" style={{
                transform: stepsVisible ? "rotate(-6deg) scaleX(1)" : "rotate(-6deg) scaleX(0)",
                transformOrigin: "left center",
                transitionDelay: `${i * 200 + 400}ms`
              }} />
                    <div className="absolute right-0 top-1/2 w-1.5 h-1.5 -translate-y-1/2 bg-primary transition-transform ease-out" style={{
                transform: stepsVisible ? "scale(1)" : "scale(0)",
                transitionDelay: `${i * 200 + 1100}ms`,
                transitionDuration: "300ms",
                transitionTimingFunction: "cubic-bezier(0.34, 1.56, 0.64, 1)"
              }} />
                  </div>

                  {}
                  <div className="md:hidden absolute left-1/2 -translate-x-1/2 -bottom-12 w-px h-12 pointer-events-none overflow-visible">
                    <div className="absolute inset-0 bg-primary origin-top transition-transform duration-700 ease-out" style={{
                transform: stepsVisible ? "rotate(-6deg) scaleY(1)" : "rotate(-6deg) scaleY(0)",
                transformOrigin: "top center",
                transitionDelay: `${i * 200 + 400}ms`
              }} />
                    <div className="absolute left-1/2 -translate-x-1/2 bottom-0 w-1.5 h-1.5 bg-primary transition-transform ease-out" style={{
                transform: stepsVisible ? "scale(1)" : "scale(0)",
                transitionDelay: `${i * 200 + 1100}ms`,
                transitionDuration: "300ms",
                transitionTimingFunction: "cubic-bezier(0.34, 1.56, 0.64, 1)"
              }} />
                  </div>
                </>}

              {}
              <div className={`relative transition-all duration-[520ms] ${stepsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`} style={{
            transitionTimingFunction: "cubic-bezier(0.2, 0.8, 0.2, 1)",
            transitionDelay: `${i * 120}ms`
          }}>
                {}
                <div className="font-default leading-none" style={{
              fontSize: "clamp(5rem, 8vw, 7rem)",
              color: "var(--primary)",
              letterSpacing: "-0.02em"
            }}>
                  {step.number}
                </div>

                {}
                <h3 className="mt-6 font-default text-foreground uppercase" style={{
              fontSize: "clamp(1.75rem, 3vw, 2.75rem)",
              letterSpacing: "0em",
              lineHeight: "0.92"
            }}>
                  {step.title}
                </h3>

                {}
                <div className="mt-5 font-mono text-foreground uppercase" style={{
              fontSize: "0.75rem",
              letterSpacing: "0.14em",
              lineHeight: "1.4"
            }}>
                  {step.caption}
                </div>

                {}
                <div className="mt-5 w-12 h-px bg-border" />

                {}
                <p className="mt-5 font-sans text-muted-foreground" style={{
              fontSize: "clamp(1.0625rem, 1.2vw, 1.25rem)",
              lineHeight: "1.55",
              letterSpacing: "-0.005em"
            }}>
                  {step.description}
                </p>
              </div>
            </div>)}
        </div>
      </div>
    </section>;
}